/**
 * 图片打包下载相关类型定义
 * 用于批量图片ZIP打包和下载
 */

// ==================== 图片打包任务状态枚举 ====================

/**
 * 图片打包任务状态
 */
export const IMAGE_PACK_STATUS = {
  /** 等待中 - 任务已创建,等待Worker处理 */
  PENDING: 'pending',
  /** 处理中 - 正在下载图片和打包 */
  PROCESSING: 'processing',
  /** 已完成 - ZIP文件已生成并上传 */
  COMPLETED: 'completed',
  /** 失败 - 打包过程中出错 */
  FAILED: 'failed',
} as const

export type ImagePackStatus =
  (typeof IMAGE_PACK_STATUS)[keyof typeof IMAGE_PACK_STATUS]

/**
 * 图片打包任务状态中文映射
 */
export const IMAGE_PACK_STATUS_MAP: Record<
  ImagePackStatus,
  { text: string; color: string }
> = {
  [IMAGE_PACK_STATUS.PENDING]: { text: '排队中', color: 'text-muted-foreground' },
  [IMAGE_PACK_STATUS.PROCESSING]: { text: '打包中', color: 'text-blue-500' },
  [IMAGE_PACK_STATUS.COMPLETED]: { text: '已完成', color: 'text-emerald-500' },
  [IMAGE_PACK_STATUS.FAILED]: { text: '打包失败', color: 'text-destructive' },
}

/**
 * 图片打包关联类型
 */
export const IMAGE_PACK_REF_TYPE = {
  /** 批量任务 */
  BATCH_TASK: 'batch_task',
  /** 工作流 */
  WORKFLOW: 'workflow',
  /** 采集批次 */
  CRAWL_BATCH: 'crawl_batch',
} as const

export type ImagePackRefType =
  (typeof IMAGE_PACK_REF_TYPE)[keyof typeof IMAGE_PACK_REF_TYPE]

// ==================== API 请求/响应类型 ====================

/**
 * 创建图片打包任务请求参数
 */
export interface CreateImagePackRequest {
  /** 关联类型 */
  ref_type: ImagePackRefType
  /** 关联ID (batch_task_id 或 workflow_id) */
  ref_id: string
}

/**
 * 创建图片打包任务响应
 */
export interface CreateImagePackResponse {
  /** 打包任务ID */
  id: string
  /** 任务状态 */
  status: ImagePackStatus
  /** 图片总数 */
  total_count: number
  /** 已处理数量 */
  processed_count: number
  /** 进度百分比 (0-100) */
  progress: number
  /** ZIP 文件名（不含扩展名） */
  zip_filename?: string
  /** 创建时间 */
  created_at: string
}

/**
 * 图片打包任务详情
 */
export interface ImagePackTask {
  /** 打包任务ID */
  id: string
  /** 用户ID */
  user_id?: string
  /** 公司ID */
  company_id?: string
  /** 关联类型 */
  ref_type: ImagePackRefType
  /** 关联ID */
  ref_id: string
  /** 任务状态 */
  status: ImagePackStatus
  /** 图片总数 */
  total_count: number
  /** 已处理数量 */
  processed_count: number
  /** 进度百分比 (0-100) */
  progress: number
  /** ZIP文件下载URL (完成时可用) */
  zip_url: string | null
  /** ZIP文件大小(字节) */
  zip_size: number | null
  /** ZIP 文件名（不含扩展名） */
  zip_filename?: string
  /** 错误信息 (失败时可用) */
  error_message: string | null
  /** 创建时间 */
  created_at: string
  /** 开始处理时间 */
  started_at: string | null
  /** 完成时间 */
  completed_at: string | null
  /** 过期时间 (ZIP链接7天有效) */
  expired_at: string | null
}

/**
 * 图片打包任务列表响应
 */
export interface ImagePackTaskListResponse {
  /** 打包任务列表 */
  items: ImagePackTask[]
  /** 总记录数 */
  total: number
  /** 当前页码 */
  page: number
  /** 每页数量 */
  page_size: number
}

/**
 * 图片打包任务列表查询参数
 */
export interface ImagePackTaskListParams {
  /** 页码 */
  page?: number
  /** 每页数量 */
  page_size?: number
  /** 关联类型筛选 */
  ref_type?: ImagePackRefType
  /** 关联ID筛选 */
  ref_id?: string
  /** 状态筛选 */
  status?: ImagePackStatus
}

/**
 * API 通用响应包装
 */
export interface ImagePackApiResponse<T> {
  /** 响应码 */
  code: number
  /** 响应消息 */
  message: string
  /** 响应数据 */
  data: T
}
